// Custom Okta Authentication for SPA with PKCE
// Uses custom auth server "Vision" with groups in ID token
// Configuration is environment-aware (auto-detects from hostname)

import { getOktaConfigSync, getEnvironment } from './secretsManager';

// Get environment-specific config
const getConfig = () => {
  const config = getOktaConfigSync();
  return {
    issuer: config.issuer,
    clientId: config.clientId,
    redirectUri: config.redirectUri,
    postLogoutRedirectUri: config.postLogoutRedirectUri,
    scopes: config.scopes,
    tokenEndpoint: `${config.issuer}/v1/token`
  };
};

// Initialize config (will be refreshed on each use)
let OKTA_CONFIG = getConfig();

// Log environment on load
console.log(`[CustomOktaAuth] Environment: ${getEnvironment()}, Client ID: ${OKTA_CONFIG.clientId}`);

class CustomOktaAuth {
  constructor() {
    this.tokenKey = 'okta_access_token';
    this.idTokenKey = 'okta_id_token';
    this.expiresAtKey = 'okta_token_expires_at';
  }

  // Generate random string for PKCE
  generateRandomString(length) {
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let text = '';
    for (let i = 0; i < length; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }

  // Generate SHA256 hash
  async sha256(plain) {
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return hash;
  }

  // Base64 URL encode
  base64urlencode(arrayBuffer) {
    const bytes = new Uint8Array(arrayBuffer);
    let str = '';
    bytes.forEach(b => str += String.fromCharCode(b));
    return btoa(str)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  // Generate PKCE challenge
  async generatePKCEChallenge() {
    const codeVerifier = this.generateRandomString(128);
    const hashed = await this.sha256(codeVerifier);
    const codeChallenge = this.base64urlencode(hashed);
    
    sessionStorage.setItem('pkce_code_verifier', codeVerifier);
    localStorage.setItem('pkce_code_verifier_backup', codeVerifier);
    
    return { codeVerifier, codeChallenge };
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = localStorage.getItem(this.tokenKey);
    const expiresAt = localStorage.getItem(this.expiresAtKey);
    
    if (!token || !expiresAt) return false;
    
    return Date.now() < parseInt(expiresAt);
  }

  // Get access token
  getAccessToken() {
    return localStorage.getItem(this.tokenKey);
  }

  // Store tokens
  storeTokens(accessToken, idToken, expiresIn) {
    const expiresAt = Date.now() + (expiresIn * 1000);
    localStorage.setItem(this.tokenKey, accessToken);
    localStorage.setItem(this.idTokenKey, idToken);
    localStorage.setItem(this.expiresAtKey, expiresAt.toString());
  }

  // Clear tokens
  clearTokens() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.idTokenKey);
    localStorage.removeItem(this.expiresAtKey);
    localStorage.removeItem('pkce_code_verifier_backup');
    sessionStorage.removeItem('pkce_code_verifier');
    sessionStorage.removeItem('pkce_state');
  }

  // Initiate login flow
  async login() {
    // Refresh config to get current environment settings
    OKTA_CONFIG = getConfig();
    
    const { codeChallenge } = await this.generatePKCEChallenge();
    const state = this.generateRandomString(32);
    
    sessionStorage.setItem('pkce_state', state);
    localStorage.setItem('pkce_state_backup', state);
    
    const params = new URLSearchParams({
      client_id: OKTA_CONFIG.clientId,
      response_type: 'code',
      scope: OKTA_CONFIG.scopes.join(' '),
      redirect_uri: OKTA_CONFIG.redirectUri,
      state: state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256'
    });
    
    window.location.href = `${OKTA_CONFIG.issuer}/v1/authorize?${params.toString()}`;
  }

  // Handle callback
  async handleCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    if (!code || !state) {
      throw new Error('Missing code or state parameter');
    }
    
    // Verify state
    const storedState = sessionStorage.getItem('pkce_state') || localStorage.getItem('pkce_state_backup');
    if (state !== storedState) {
      throw new Error('State mismatch');
    }
    
    // Get code verifier
    const codeVerifier = sessionStorage.getItem('pkce_code_verifier') || localStorage.getItem('pkce_code_verifier_backup');
    console.log('Code verifier found:', codeVerifier ? `Yes (${codeVerifier.length} chars)` : 'No');
    
    if (!codeVerifier) {
      throw new Error('Code verifier not found in storage');
    }
    
    // SPA exchanges tokens directly with Okta using PKCE (no client secret needed)
    // Use application/x-www-form-urlencoded as per OAuth 2.0 spec
    const tokenParams = new URLSearchParams({
      grant_type: 'authorization_code',
      code: code,
      redirect_uri: OKTA_CONFIG.redirectUri,
      client_id: OKTA_CONFIG.clientId,
      code_verifier: codeVerifier
    });
    
    console.log('Exchanging code for tokens directly with Okta:', OKTA_CONFIG.tokenEndpoint);
    
    const response = await fetch(OKTA_CONFIG.tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json'
      },
      body: tokenParams.toString()
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token exchange failed:', response.status, errorText);
      let error;
      try {
        error = JSON.parse(errorText);
      } catch (e) {
        error = { error: errorText };
      }
      const errorMsg = error.error_description || error.message || error.error || `Token exchange failed (${response.status})`;
      throw new Error(`${errorMsg}\n\nDebug: ${errorText}`);
    }
    
    const tokens = await response.json();
    console.log('Token exchange successful!');
    this.storeTokens(tokens.access_token, tokens.id_token, tokens.expires_in || 3600);
    
    // Clean up PKCE data
    sessionStorage.removeItem('pkce_code_verifier');
    sessionStorage.removeItem('pkce_state');
    localStorage.removeItem('pkce_code_verifier_backup');
    localStorage.removeItem('pkce_state_backup');
    
    console.log('Authentication successful!');
    return tokens;
  }

  // Decode JWT token
  decodeToken(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  // Check if user is admin - CLIENT-SIDE ONLY (for UI display)
  // ⚠️ SECURITY NOTE: This is NOT for authorization - only for UI display
  // Actual authorization must be enforced by backend API Gateway authorizer
  // Groups are passed via ID token from custom auth server "Vision"
  // Admin group: App-VISION-Admin–Dev (note: en-dash in group name)
  isAdmin() {
    const idToken = localStorage.getItem(this.idTokenKey);
    if (!idToken) {
      console.log('[isAdmin] No ID token found');
      return false;
    }
    
    const decoded = this.decodeToken(idToken);
    if (!decoded) {
      console.log('[isAdmin] Failed to decode ID token');
      return false;
    }
    
    // Debug: Log the decoded token to see what fields are available
    console.log('[isAdmin] Decoded ID token fields:', Object.keys(decoded));
    
    // Groups claim - Okta team confirmed it's configured:
    // Name: groups
    // Include in: ID Token and Access Token
    // Value type: Groups
    // Filter: .* (all groups) - Note: User requested App-VISION* filter
    const groups = decoded.groups || 
                   decoded.Groups || 
                   decoded['groups'] || 
                   decoded['Groups'] || 
                   [];
    
    console.log('[isAdmin] Groups found:', groups);
    console.log('[isAdmin] Groups type:', typeof groups, Array.isArray(groups));
    
    // If no groups found, provide helpful message
    if (!groups || (Array.isArray(groups) && groups.length === 0)) {
      const tokenIssuedAt = decoded.iat ? new Date(decoded.iat * 1000).toISOString() : 'unknown';
      console.warn('[isAdmin] ⚠️ Groups claim not found in ID token');
      console.warn('[isAdmin] Token issued at:', tokenIssuedAt);
      console.warn('[isAdmin] If groups claim was just added to auth server, you need a fresh token:');
      console.warn('[isAdmin] 1. Click logout');
      console.warn('[isAdmin] 2. Clear browser storage (optional but recommended)');
      console.warn('[isAdmin] 3. Log back in to get a new token with groups');
    }
    
    // If groups is not an array, try to extract from object
    let groupsArray = groups;
    if (!Array.isArray(groups)) {
      if (typeof groups === 'object' && groups !== null) {
        // Try to extract array from object
        groupsArray = Object.values(groups).filter(v => typeof v === 'string');
        console.log('[isAdmin] Extracted groups from object:', groupsArray);
      } else if (typeof groups === 'string') {
        // Single group as string
        groupsArray = [groups];
        console.log('[isAdmin] Single group as string:', groupsArray);
      } else {
        groupsArray = [];
      }
    }
    
    if (!Array.isArray(groupsArray) || groupsArray.length === 0) {
      console.log('[isAdmin] No groups found in ID token');
      console.log('[isAdmin] Available token fields:', Object.keys(decoded));
      return false;
    }
    
  // ⚠️ SECURITY WARNING: This is a CLIENT-SIDE check for UI display ONLY!
  // This can be bypassed by modifying JavaScript, localStorage, or tokens.
  // Do NOT use this for authorization - all authorization must happen on the backend.
  // This method is only for showing/hiding UI elements (UX).
  
  // Check if user is member of the admin group
  // Group names from token: App-VISION-Admin-Dev, App-VISION-Admin-Prod, etc.
  // Note: Token shows regular hyphen (-), not en-dash
  const isAdminUser = groupsArray.some(g => {
      // Handle both string and object formats
      const groupName = typeof g === 'string' ? g : (g.name || g.id || String(g));
      // Normalize group name (handle both en-dash and regular hyphen)
      const groupNormalized = groupName.replace(/[–—]/g, '-').toLowerCase();
      // Check for admin groups (Dev, Prod, Test, Int environments)
      const matches = groupNormalized === 'app-vision-admin-dev' || 
                      groupNormalized === 'app-vision-admin-prod' ||
                      groupNormalized === 'app-vision-admin-test' ||
                      groupNormalized === 'app-vision-admin-int' ||
                      groupNormalized.includes('app-vision-admin');
      if (matches) {
        console.log('[isAdmin] Admin group matched:', groupName);
      }
      return matches;
    });
    
    console.log('[isAdmin] Final admin check result:', isAdminUser);
    return isAdminUser;
  }

  // ⚠️ SECURITY WARNING: This is a CLIENT-SIDE check for UI display ONLY!
  // This can be bypassed by modifying JavaScript, localStorage, or tokens.
  // Do NOT use this for authorization - all authorization must happen on the backend.
  // This method is only for showing/hiding UI elements (UX).
  
  // Check if user is member of the user group (App-VISION-User-Dev)
  hasUserGroup() {
    const idToken = localStorage.getItem(this.idTokenKey);
    if (!idToken) {
      console.log('[hasUserGroup] No ID token found');
      return false;
    }

    const decoded = this.decodeToken(idToken);
    if (!decoded) {
      console.log('[hasUserGroup] Failed to decode ID token');
      return false;
    }

    // Try multiple possible claim names for groups
    const groups = decoded.groups || 
                   decoded.Groups || 
                   decoded.group || 
                   decoded.Group || 
                   decoded.groupMembership ||
                   decoded['groups'] || 
                   decoded['Groups'] || 
                   [];

    // If groups is not an array, try to extract from object
    let groupsArray = groups;
    if (!Array.isArray(groups)) {
      if (typeof groups === 'object' && groups !== null) {
        groupsArray = Object.values(groups).filter(v => typeof v === 'string');
      } else if (typeof groups === 'string') {
        groupsArray = [groups];
      } else {
        groupsArray = [];
      }
    }

    if (!Array.isArray(groupsArray) || groupsArray.length === 0) {
      console.log('[hasUserGroup] No groups found in ID token');
      return false;
    }

    // Check if user is member of the user group
    // Group names: App-VISION-User-Dev, App-VISION-User-Prod, etc.
    const hasUserGroup = groupsArray.some(g => {
      const groupName = typeof g === 'string' ? g : (g.name || g.id || String(g));
      const groupNormalized = groupName.replace(/[–—]/g, '-').toLowerCase();
      // Check for user groups (Dev, Prod, Test, Int environments)
      const matches = groupNormalized === 'app-vision-user-dev' || 
                      groupNormalized === 'app-vision-user-prod' ||
                      groupNormalized === 'app-vision-user-test' ||
                      groupNormalized === 'app-vision-user-int' ||
                      groupNormalized.includes('app-vision-user');
      if (matches) {
        console.log('[hasUserGroup] User group matched:', groupName);
      }
      return matches;
    });

    console.log('[hasUserGroup] Final user group check result:', hasUserGroup);
    return hasUserGroup;
  }

  // ⚠️ SECURITY WARNING: This is a CLIENT-SIDE check for UI display ONLY!
  // This can be bypassed by modifying JavaScript, localStorage, or tokens.
  // Do NOT use this for authorization - all authorization must happen on the backend.
  // This method is only for showing/hiding UI elements (UX).
  // The ProtectedRoute uses this for initial UI rendering, but backend should validate all requests.
  
  // Check if user has required access (either admin or user group)
  hasRequiredAccess() {
    const isAdmin = this.isAdmin();
    const hasUser = this.hasUserGroup();
    const hasAccess = isAdmin || hasUser;
    console.log('[hasRequiredAccess] Admin:', isAdmin, 'User:', hasUser, 'Has Access:', hasAccess);
    return hasAccess;
  }

  // Check if user is admin - SECURE: Use backend API with authorizer
  // The vision-api-authorizer Lambda validates JWT and checks groups server-side
  // This is the secure way to check admin status
  async checkAdminStatus() {
    try {
      // Import API service dynamically to avoid circular dependency
      const apiModule = await import('./api');
      const api = apiModule.default;
      const isAdmin = await api.checkAdminStatus();
      console.log('[checkAdminStatus] Backend admin check result:', isAdmin);
      return isAdmin;
    } catch (error) {
      console.warn('[checkAdminStatus] Backend check failed:', error);
      // Throw error so caller can handle fallback
      // This allows getUserInfo() to fall back to client-side check
      throw error;
    }
  }

  // Get user info
  async getUserInfo() {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      throw new Error('No access token');
    }
    
    // Get user info from userinfo endpoint
    const response = await fetch(`${OKTA_CONFIG.issuer}/v1/userinfo`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to get user info');
    }
    
    const userInfo = await response.json();
    
    // Try to get groups from multiple sources
    let groups = [];
    
    // 1. First, try ID token (primary source for custom auth server)
    const idToken = localStorage.getItem(this.idTokenKey);
    if (idToken) {
      const decoded = this.decodeToken(idToken);
      if (decoded) {
        console.log('[getUserInfo] Checking ID token for groups...');
        // Check all possible group claim names
        const groupsRaw = decoded.groups || 
                         decoded.Groups || 
                         decoded.group || 
                         decoded.Group ||
                         decoded.groupMembership ||
                         decoded['groups'] || 
                         decoded['Groups'] ||
                         decoded['groupMembership'] ||
                         [];
        
        // Ensure groups is an array
        if (Array.isArray(groupsRaw)) {
          groups = groupsRaw;
        } else if (typeof groupsRaw === 'string') {
          groups = [groupsRaw];
        } else if (typeof groupsRaw === 'object' && groupsRaw !== null) {
          groups = Object.values(groupsRaw).filter(v => typeof v === 'string');
        }
        
        if (groups.length > 0) {
          console.log('[getUserInfo] Groups found in ID token:', groups);
        }
      }
    }
    
    // 2. If no groups in ID token, try userinfo endpoint
    if (groups.length === 0 && userInfo.groups) {
      groups = Array.isArray(userInfo.groups) ? userInfo.groups : [userInfo.groups];
      console.log('[getUserInfo] Groups found in userinfo:', groups);
    }
    
    // 3. If still no groups, try fetching from Okta Groups API (requires additional scope/permissions)
    // This would require the user to have the groups scope and the API to support it
    
    // Add groups to user info (for display purposes only)
    userInfo.groups = groups;
    
    // ⚠️ SECURITY WARNING: Client-side checks are for UI display ONLY, not authorization!
    // All actual authorization MUST be enforced on the backend via API Gateway authorizer.
    // Client-side checks can be bypassed by modifying JavaScript, localStorage, or tokens.
    // 
    // SECURE APPROACH:
    // 1. Backend endpoint validates JWT and checks groups server-side
    // 2. All admin API endpoints use vision-api-authorizer Lambda
    // 3. Backend denies access if user doesn't have required groups
    // 4. Client-side check is ONLY for showing/hiding UI elements (UX)
    try {
      // Try backend check first (secure)
      userInfo.isAdmin = await this.checkAdminStatus();
      console.log('[getUserInfo] ✅ Admin status from backend (secure):', userInfo.isAdmin);
    } catch (error) {
      // Backend endpoint unavailable - use client-side check for UI display ONLY
      // ⚠️ WARNING: This is NOT secure! Users can bypass this by modifying client code.
      // This should only be used for UI display. All actual operations must be protected by backend.
      console.error('[getUserInfo] ⚠️ SECURITY WARNING: Backend check unavailable, using client-side check for UI only:', error.message);
      console.error('[getUserInfo] ⚠️ Client-side checks can be bypassed - backend endpoint MUST be implemented for production!');
      
      // Use client-side check ONLY for UI display (not for authorization)
      const clientSideAdmin = this.isAdmin();
      userInfo.isAdmin = clientSideAdmin;
      console.warn('[getUserInfo] Using client-side admin check (UI display only, NOT secure):', clientSideAdmin);
      
      // In production, you might want to deny access by default when backend is unavailable
      // Uncomment the line below to enforce secure-by-default behavior:
      // userInfo.isAdmin = false; // Deny access when backend unavailable (secure default)
    }
    
    console.log('[getUserInfo] Final user info:', {
      name: userInfo.name,
      email: userInfo.email,
      groups: userInfo.groups,
      isAdmin: userInfo.isAdmin
    });
    
    return userInfo;
  }

  // Logout
  async logout() {
    // Clear local tokens
    this.clearTokens();
    
    // Redirect to Okta home page
    window.location.href = 'https://dish.okta.com';
  }
}

export const customOktaAuth = new CustomOktaAuth();
export default customOktaAuth;

